import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDropdownPlacement } from '../../../hooks/useDropdownPlacement';
import type { Props, SearchFilterDropdownOption } from './types';

export const Presentation: FunctionComponent<Props> = ({
  name,
  options,
  selectedValues,
  handleChange,
  placeholder = '選択してください',
  onClearAll,
  onSelectAll,
  filterThreshold = 10,
  triggerLabel,
  selectedCount,
}) => {
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const { panelRef, panelStyle } = useDropdownPlacement(open, rootRef);

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      const target = event.target as Node;
      // パネルは body へ Portal されるため rootRef には含まれない。両方を除外する
      if (!rootRef.current?.contains(target) && !panelRef.current?.contains(target)) {
        setOpen(false);
        setKeyword('');
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const isSelected = (option: SearchFilterDropdownOption) =>
    option.selected ?? selectedValues.includes(option.value);

  const selectedLabels = options.filter(isSelected).map((option) => option.label);
  const visibleOptions = options.filter((option) =>
    option.label.toLowerCase().includes(keyword.toLowerCase())
  );
  const label = triggerLabel !== undefined ? triggerLabel : selectedLabels.join(' , ');
  const count = selectedCount !== undefined ? selectedCount : selectedLabels.length;

  return (
    <div className="search-form__dropdown" ref={rootRef}>
      <button
        type="button"
        className={`search-form__dropdown__trigger${open ? ' is-open' : ''}`}
        aria-expanded={open}
        onClick={() => {
          setOpen((prev) => !prev);
          setKeyword('');
        }}
      >
        <span className={`search-form__dropdown__trigger__label${label ? '' : ' placeholder'}`}>
          {label || placeholder}
        </span>
        {count > 0 && (
          <span className="search-form__dropdown__trigger__badge">{count}</span>
        )}
        <svg className={`search-form__dropdown__trigger__arrow${open ? ' is-open' : ''}`} xmlns="http://www.w3.org/2000/svg" height="10px" viewBox="267.69 -596.31 424.62 235.62" width="10px" fill="#19190F"><path d="M480-360.69 267.69-573 291-596.31l189 189 189-189L692.31-573 480-360.69Z"/></svg>
      </button>

      {open && typeof document !== 'undefined' && createPortal(
        <div ref={panelRef} className="search-form__dropdown__panel" style={panelStyle}>
          <div className="search-form__dropdown__panel__contents">
            {options.length >= filterThreshold && (
              <input
                type="text"
                value={keyword}
                placeholder="絞り込む"
                aria-label="絞り込む"
                onChange={(event) => setKeyword(event.currentTarget.value)}
                autoFocus
              />
            )}
            {visibleOptions.length > 0 ? (
              <ul>
                {visibleOptions.map((option) => (
                  <li key={`${option.name || name}_${option.value}`} className={isSelected(option) ? 'selected' : ''}>
                    <label>
                      {option.label}
                      <input
                        type="checkbox"
                        name={option.name || name}
                        value={option.value}
                        checked={isSelected(option)}
                        onChange={handleChange}
                        className="checkbox-input"
                      />
                      {isSelected(option) && (
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="10" viewBox="0 0 14 10" fill="none"><path d="M4.88542 9.89714L0.21875 5.23047L1.11458 4.33464L4.88542 8.10547L12.8854 0.105469L13.7812 1.0013L4.88542 9.89714Z" fill="#19190F" /></svg>
                      )}
                    </label>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="search-form__dropdown__panel__contents__empty">該当する項目はありません</p>
            )}
          </div>
          {(onClearAll || onSelectAll) && (
            <footer>
              {onClearAll && (
                <button type="button" onClick={onClearAll}>
                  すべてクリア
                </button>
              )}
              {onSelectAll && (
                <button type="button" onClick={onSelectAll}>
                  すべて選択
                </button>
              )}
            </footer>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
};
